<template>
  <div class="h-full flex flex-col bg-[#f8fafc] overflow-auto">
    <!-- Edit User Modal -->
    <UserModal
      :isOpen="showEditModal"
      :editUser="editingUser"
      @close="closeEditModal"
      @update="handleUpdate"
    />
    <!-- View User Modal -->
    <ViewUserModal
      :isOpen="showViewModal"
      :user="viewingUser"
      @close="closeViewModal"
    />
    <!-- Controls Section: Search aligned right -->
    <div class="flex justify-end items-center mb-4 gap-3">
      <input 
        v-model="searchQuery"
        type="text" 
        class="w-[300px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
        placeholder="Search by name, phone or username..."
      >
    </div>

    <!-- Table: Matching testTable.html exactly -->
    <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
      <thead>
        <tr>
          <th>No.</th>
          <th @click="sortBy('full_name')" class="sort-col text-[12px]">
            Full Name <span v-if="sortKey === 'full_name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('phone_number')" class="sort-col text-[12px]">
            Phone <span v-if="sortKey === 'phone_number'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('role')" class="sort-col text-[12px]">
            Role <span v-if="sortKey === 'role'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('departments')" class="sort-col text-[12px]">
            Department <span v-if="sortKey === 'departments'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('username')" class="sort-col text-[12px]">
            Username <span v-if="sortKey === 'username'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('salary')" class="sort-col text-[12px]">
            Salary <span v-if="sortKey === 'salary'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('created_at')" class="sort-col text-[12px]">
            Created At <span v-if="sortKey === 'created_at'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('status')" class="sort-col text-[12px]">
            Status <span v-if="sortKey === 'status'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th class="text-[12px]">Actions</th>
        </tr>
      </thead>
      <tbody class="text-[12px]">
        <tr v-for="(user, index) in filteredUsers" :key="user.id" class="hover:bg-slate-100/50 transition-colors border-b">
          <td class="font-medium text-slate-500">{{ index + 1 }}</td>
          <td class="font-bold text-[#0d4169]">{{ user.full_name }}</td>
          <td>{{ user.phone_number }}</td>
          <td>
            <span class="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-600 border border-slate-200 uppercase tracking-tighter whitespace-nowrap inline-block" v-if="user.role">
              {{ user.role.split(',')[0].trim() }}{{ (user.role && user.role.split(',').length > 1) ? ' +' + (user.role.split(',').length - 1) : '' }}
            </span>
          </td>
          <td>
            <span class="px-2 py-0.5 bg-indigo-50 rounded text-[10px] font-bold text-indigo-600 border border-indigo-200 uppercase tracking-tighter whitespace-nowrap inline-block" v-if="user.departments">
              {{ user.departments.split(',')[0].trim() }}{{ (user.departments && user.departments.split(',').length > 1) ? ' +' + (user.departments.split(',').length - 1) : '' }}
            </span>
          </td>
          <td class="text-slate-500 font-medium">{{ user.username }}</td>
          <td class="text-slate-600 font-medium">{{ formatSalary(user.salary) }}</td>
          <td class="text-slate-400 italic">{{ formatDate(user.created_at) }}</td>
          <td>
            <span class="status-pill" :class="user.status === 1 ? 'active' : 'inactive'">
              {{ user.status === 1 ? 'Active' : 'Inactive' }}
            </span>
          </td>
          <td class="actions">
            <button
              @click="viewUser(user)"
              class="view-btn"
              :class="{ 'opacity-50 cursor-not-allowed action-disabled-marker': !canViewUsers }"
              :disabled="!canViewUsers"
              title="View Details"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </button>
            <button
              @click="editUser(user)"
              class="text-emerald-600 edit-btn"
              :class="canEditUsers ? 'hover:underline' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
              :disabled="!canEditUsers"
            >
              Edit
            </button>
            <label class="toggle-switch" :class="{ 'action-disabled-marker': !canEditUsers }">
              <input type="checkbox" :checked="user.status === 1" :disabled="!canEditUsers" @change="toggleUserStatus(user)">
              <span class="slider"></span>
            </label>
          </td>
        </tr>
        <tr v-if="loading">
  <td colspan="10" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>
        <tr v-else-if="filteredUsers.length === 0">
          <td colspan="10" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
            No users available.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import UserModal from './UserModal.vue'
import ViewUserModal from './ViewUserModal.vue'
import { apiFetch, WS_BASE_URL } from '../../utils/api'
import store from '../../store'

const users = ref([])
const searchQuery = ref('')
const sortKey = ref('full_name')
const sortOrder = ref('asc')
const showEditModal = ref(false)
const showViewModal = ref(false)
const editingUser = ref(null)
const viewingUser = ref(null)
const loading = ref(false)
let userSyncSocket = null
let userSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)

const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})

const hasAdminPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

const usersPrivilege = computed(() => {
  return userPrivileges.value?.Users || userPrivileges.value?.users || null
})

const canEditUsers = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!usersPrivilege.value?.Edit
})

const canViewUsers = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!usersPrivilege.value?.View
})

async function loadUsers() {
  if (!currentUser.value) return
  
  loading.value = true
  try {
    const branchId = currentUser.value.branchId || currentUser.value.branch_id
    const companyId = currentUser.value.companyId || (currentUser.value.company ? currentUser.value.company.id : null)
    
    let url = ''
    if (branchId) {
      url = `users/branch/${branchId}`
    } else if (companyId) {
      url = `users/company/${companyId}`
    } else {
      console.error('No branch or company ID found in token')
      return
    }

    const response = await apiFetch(url)
    if (response.ok) {
      users.value = await response.json()
    } else {
      console.error('Failed to fetch users')
    }
  } catch (error) {
    console.error('Error loading users:', error)
  } finally {
    loading.value = false
  }
}

const filteredUsers = computed(() => {
  let result = [...users.value]

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(user => 
      (user.full_name || '').toLowerCase().includes(query) ||
      (user.username || '').toLowerCase().includes(query) ||
      (user.phone_number || '').includes(query)
    )
  }

  result.sort((a, b) => {
    let modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] || '').toString().toLowerCase()
    const valB = (b[sortKey.value] || '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })

  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function editUser(user) {
  if (!canEditUsers.value) return
  editingUser.value = user
  showEditModal.value = true
}

function closeEditModal() {
  showEditModal.value = false
  editingUser.value = null
}

function viewUser(user) {
  if (!canViewUsers.value) return
  viewingUser.value = user
  showViewModal.value = true
}

function closeViewModal() {
  showViewModal.value = false
  viewingUser.value = null
}

async function handleUpdate(updatedUser) {
  try {
    const response = await apiFetch(`users/${updatedUser.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...updatedUser,
        updated_by: currentUser.value.id || currentUser.value.sub
      })
    })

    if (response.ok) {
      loadUsers()
      closeEditModal()
    } else {
      const err = await response.json()
      alert(err.message || 'Failed to update user')
    }
  } catch (error) {
    console.error('Error updating user:', error)
    alert('An error occurred while updating the user')
  }
}

async function toggleUserStatus(user) {
  if (!canEditUsers.value) return
  try {
    const newStatus = user.status === 1 ? 0 : 1
    const response = await apiFetch(`users/${user.id}`, {
      method: 'PUT',
      body: JSON.stringify({ 
        status: newStatus,
        updated_by: currentUser.value.id || currentUser.value.sub
      })
    })

    if (response.ok) {
      loadUsers()
    } else {
      alert('Failed to update status')
    }
  } catch (error) {
    console.error('Error toggling status:', error)
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function formatSalary(value) {
  if (value === null || value === undefined || value === '') return '-'
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function disconnectUserSyncRealtime() {
  if (userSyncReconnectTimer) {
    clearTimeout(userSyncReconnectTimer)
    userSyncReconnectTimer = null
  }
  if (userSyncSocket) {
    try {
      userSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    userSyncSocket = null
  }
}

function connectUserSyncRealtime() {
  disconnectUserSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const companyId = user.companyId || user.company_id || user.company?.id
  if (!companyId) return

  const branchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  userSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: companyId,
      branch_id: branchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('user.')) {
        loadUsers()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    userSyncSocket = null
    userSyncReconnectTimer = setTimeout(() => {
      connectUserSyncRealtime()
    }, 3000)
  })
}

onMounted(() => {
  loadUsers()
  connectUserSyncRealtime()
  window.addEventListener('users-updated', loadUsers)
})
onUnmounted(() => {
  disconnectUserSyncRealtime()
  window.removeEventListener('users-updated', loadUsers)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}

th, td {
  padding: 3px 15px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f8fafc;
}

.status-pill {
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: bold;
}
.active  { background: #dcfce7; color: #166534; }
.inactive { background: #fee2e2; color: #991b1b; }

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 11px;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions .view-btn {
  color: #64748b;
  text-decoration: none;
  font-size: 10px;
}

.actions .edit-btn {
  font-size: 0.85rem;
}

.actions .view-btn:hover {
  color: #0d4169;
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 24px;
  height: 13px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 8px;
  height: 8px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(11px);
}
</style>